import { ref } from 'vue';
import globalShared from '@/shared';
import { findMyStickerCollections } from '@/graphs/gql';
import type { MyStickerCollection } from '@/data/stickerCollection.types';

export function useMyStickerCollections() {
  const {
    makeGraphRequest, getAuthToken, ElMessage, store,
  } = globalShared();

  const collections = ref<MyStickerCollection[]>([]);
  const loading = ref(false);
  const loaded = ref(false);

  async function fetchMyCollections(
    options?: { force?: boolean; silentErrors?: boolean },
  ) {
    if (loaded.value && !options?.force) {
      return collections.value;
    }

    loading.value = true;
    try {
      const response = await makeGraphRequest(
        'query',
        findMyStickerCollections,
        // Collections of whoever the user is acting as (themselves or an organization)
        { orgId: store.getters['actingEntity/orgId'] ?? null },
        getAuthToken(),
      );

      if (response?.errors?.length) {
        console.error('findMyStickerCollections errors:', response.errors);
        if (!options?.silentErrors) {
          ElMessage.error(response.errors[0]?.message || 'Failed to load collections');
        }
        collections.value = [];
        return [];
      }

      collections.value = response?.data?.findMyStickerCollections ?? [];
      loaded.value = true;
      return collections.value;
    } catch (error) {
      console.error('findMyStickerCollections failed:', error);
      if (!options?.silentErrors) {
        ElMessage.error('Failed to load collections');
      }
      collections.value = [];
      return [];
    } finally {
      loading.value = false;
    }
  }

  function resetCollections() {
    collections.value = [];
    loaded.value = false;
  }

  return {
    collections,
    loading,
    loaded,
    fetchMyCollections,
    resetCollections,
  };
}
